import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, BookOpen, Users, Sliders, Brain, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { getAllPlays } from '../../services/playsService';
import { aiQuizGenerationService } from '../../services/aiQuizGenerationService';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

/**
 * AI Quiz Generator Page
 * Allows educators to generate contextual quiz questions using OpenAI
 */
const AIQuizGenerator = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // State management
  const [plays, setPlays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Form state
  const [selectedPlay, setSelectedPlay] = useState(null);
  const [availableScenes, setAvailableScenes] = useState([]);
  const [availableCharacters, setAvailableCharacters] = useState([]);
  const [selectedScenes, setSelectedScenes] = useState([]);
  const [selectedCharacters, setSelectedCharacters] = useState([]);
  const [difficulty, setDifficulty] = useState('medium');
  const [questionCount, setQuestionCount] = useState(10);
  const [questionTypes, setQuestionTypes] = useState(['multiple_choice']);
  const [quizTitle, setQuizTitle] = useState('');
  const [quizDescription, setQuizDescription] = useState('');

  // Load plays on mount
  useEffect(() => {
    loadPlays();
  }, []);

  // Load scenes when play is selected
  useEffect(() => {
    if (selectedPlay) {
      loadPlayScenes(selectedPlay?.id);
      loadPlayCharacters(selectedPlay?.id);
      setQuizTitle(`${selectedPlay?.title} Quiz`);
      setQuizDescription(`AI-generated quiz for ${selectedPlay?.title}`);
    }
  }, [selectedPlay]);

  const loadPlays = async () => {
    try {
      setLoading(true);
      const playsData = await getAllPlays();
      setPlays(playsData);
      setError(null);
    } catch (err) {
      setError('Failed to load plays');
    } finally {
      setLoading(false);
    }
  };

  const loadPlayScenes = async (playId) => {
    try {
      const { data: scenesData } = await supabase
        ?.from('scenes')
        ?.select('act_number, scene_number, title')
        ?.eq('play_id', playId)
        ?.order('act_number', { ascending: true })
        ?.order('scene_number', { ascending: true });

      if (scenesData) {
        setAvailableScenes(scenesData);
      }
    } catch (err) {
      console.error('Error loading scenes:', err);
    }
  };

  const loadPlayCharacters = async (playId) => {
    try {
      const characters = await aiQuizGenerationService?.getPlayCharacters(playId);
      setAvailableCharacters(characters);
    } catch (err) {
      console.error('Error loading characters:', err);
    }
  };

  const handleSceneToggle = (scene) => {
    const sceneKey = `${scene?.act_number}.${scene?.scene_number}`;
    const isSelected = selectedScenes?.some(
      s => `${s?.act}.${s?.scene}` === sceneKey
    );

    if (isSelected) {
      setSelectedScenes(selectedScenes?.filter(
        s => `${s?.act}.${s?.scene}` !== sceneKey
      ));
    } else {
      setSelectedScenes([
        ...selectedScenes,
        { act: scene?.act_number, scene: scene?.scene_number }
      ]);
    }
  };

  const handleCharacterToggle = (character) => {
    if (selectedCharacters?.includes(character)) {
      setSelectedCharacters(selectedCharacters?.filter(c => c !== character));
    } else {
      setSelectedCharacters([...selectedCharacters, character]);
    }
  };

  const handleQuestionTypeToggle = (type) => {
    if (questionTypes?.includes(type)) {
      if (questionTypes?.length > 1) {
        setQuestionTypes(questionTypes?.filter(t => t !== type));
      }
    } else {
      setQuestionTypes([...questionTypes, type]);
    }
  };

  const validateForm = () => {
    if (!selectedPlay) {
      setError('Please select a play');
      return false;
    }
    if (selectedScenes?.length === 0) {
      setError('Please select at least one scene');
      return false;
    }
    if (!quizTitle?.trim()) {
      setError('Please enter a quiz title');
      return false;
    }
    if (questionCount < 5 || questionCount > 50) {
      setError('Question count must be between 5 and 50');
      return false;
    }
    return true;
  };

  const handleGenerateQuiz = async () => {
    if (!validateForm()) return;

    try {
      setGenerating(true);
      setError(null);
      setSuccess(null);

      // Generate questions using AI
      const questions = await aiQuizGenerationService?.generateQuizQuestions({
        playId: selectedPlay?.id,
        scenes: selectedScenes,
        characters: selectedCharacters,
        difficulty,
        questionCount,
        questionTypes
      });

      // Save quiz to database
      const quizData = {
        play_id: selectedPlay?.id,
        title: quizTitle,
        description: quizDescription,
        passing_score: 70,
        time_limit_minutes: Math.ceil(questionCount * 1.5)
      };

      const result = await aiQuizGenerationService?.saveGeneratedQuiz(
        quizData,
        questions,
        user?.id
      );

      setSuccess(`Successfully generated ${questions?.length} questions!`);
      
      // Navigate to quiz preview after 2 seconds
      setTimeout(() => {
        navigate(`/admin/quiz/${result?.quiz?.id}/preview`);
      }, 2000);
    } catch (err) {
      setError(err?.message || 'Failed to generate quiz. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 flex items-center justify-center">
        <div className="flex items-center gap-3 text-white">
          <Loader2 className="w-8 h-8 animate-spin" />
          <span className="text-lg">Loading plays...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-white/10 rounded-xl backdrop-blur-sm">
              <Sparkles className="w-8 h-8 text-yellow-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">AI Quiz Generator</h1>
              <p className="text-purple-200">Generate contextual quiz questions with AI</p>
            </div>
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-400 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-100">{error}</span>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-500/20 border border-green-400 rounded-lg flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-400" />
            <span className="text-green-100">{success}</span>
          </div>
        )}

        {/* Configuration Form */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Play & Content Selection */}
          <div className="space-y-6">
            {/* Play Selection */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="w-5 h-5 text-purple-300" />
                <h3 className="text-lg font-semibold text-white">Select Play</h3>
              </div>
              <select
                value={selectedPlay?.id || ''}
                onChange={(e) => {
                  const play = plays?.find(p => p?.id === e?.target?.value);
                  setSelectedPlay(play);
                  setSelectedScenes([]);
                  setSelectedCharacters([]);
                }}
                className="w-full px-4 py-3 bg-white/5 border border-purple-300/30 rounded-lg text-white focus:outline-none focus:border-purple-400"
              >
                <option value="">Choose a play...</option>
                {plays?.map(play => (
                  <option key={play?.id} value={play?.id}>
                    {play?.title} by {play?.author}
                  </option>
                ))}
              </select>
            </div>

            {/* Scene Selection */}
            {selectedPlay && (
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">
                  Select Scenes ({selectedScenes?.length} selected)
                </h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {availableScenes?.map(scene => {
                    const sceneKey = `${scene?.act_number}.${scene?.scene_number}`;
                    const isSelected = selectedScenes?.some(
                      s => `${s?.act}.${s?.scene}` === sceneKey
                    );
                    return (
                      <button
                        key={sceneKey}
                        onClick={() => handleSceneToggle(scene)}
                        className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                          isSelected
                            ? 'bg-purple-500 text-white' :'bg-white/5 text-purple-100 hover:bg-white/10'
                        }`}
                      >
                        <div className="font-medium">
                          Act {scene?.act_number}, Scene {scene?.scene_number}
                        </div>
                        {scene?.title && (
                          <div className="text-sm opacity-75">{scene?.title}</div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Character Selection */}
            {selectedPlay && (
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-5 h-5 text-purple-300" />
                  <h3 className="text-lg font-semibold text-white">
                    Select Characters ({selectedCharacters?.length} selected)
                  </h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {availableCharacters?.map(character => {
                    const isSelected = selectedCharacters?.includes(character);
                    return (
                      <button
                        key={character}
                        onClick={() => handleCharacterToggle(character)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                          isSelected
                            ? 'bg-purple-500 text-white' :'bg-white/5 text-purple-100 hover:bg-white/10'
                        }`}
                      >
                        {character}
                      </button>
                    );
                  })}
                </div>
                <p className="text-purple-200 text-sm mt-3">
                  Leave empty to include all characters
                </p>
              </div>
            )}
          </div>

          {/* Right Column - Quiz Settings */}
          <div className="space-y-6">
            {/* Quiz Details */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Quiz Details</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-purple-200 text-sm font-medium mb-2">
                    Quiz Title
                  </label>
                  <input
                    type="text"
                    value={quizTitle}
                    onChange={(e) => setQuizTitle(e?.target?.value)}
                    placeholder="Enter quiz title..."
                    className="w-full px-4 py-3 bg-white/5 border border-purple-300/30 rounded-lg text-white placeholder-purple-300/50 focus:outline-none focus:border-purple-400"
                  />
                </div>
                <div>
                  <label className="block text-purple-200 text-sm font-medium mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    value={quizDescription}
                    onChange={(e) => setQuizDescription(e?.target?.value)}
                    placeholder="Describe the quiz focus..."
                    rows={3}
                    className="w-full px-4 py-3 bg-white/5 border border-purple-300/30 rounded-lg text-white placeholder-purple-300/50 focus:outline-none focus:border-purple-400 resize-none"
                  />
                </div>
              </div>
            </div>

            {/* Difficulty & Question Count */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <Sliders className="w-5 h-5 text-purple-300" />
                <h3 className="text-lg font-semibold text-white">Settings</h3>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-purple-200 text-sm font-medium mb-2">
                    Difficulty Level
                  </label>
                  <select
                    value={difficulty}
                    onChange={(e) => setDifficulty(e?.target?.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-purple-300/30 rounded-lg text-white focus:outline-none focus:border-purple-400"
                  >
                    <option value="easy">Easy - Basic comprehension</option>
                    <option value="medium">Medium - Theme & character analysis</option>
                    <option value="hard">Hard - Critical analysis & symbolism</option>
                  </select>
                </div>
                <div>
                  <label className="block text-purple-200 text-sm font-medium mb-2">
                    Number of Questions (5-50)
                  </label>
                  <input
                    type="number"
                    min="5"
                    max="50"
                    value={questionCount}
                    onChange={(e) => setQuestionCount(parseInt(e?.target?.value) || 10)}
                    className="w-full px-4 py-3 bg-white/5 border border-purple-300/30 rounded-lg text-white focus:outline-none focus:border-purple-400"
                  />
                </div>
              </div>
            </div>

            {/* Question Types */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <Brain className="w-5 h-5 text-purple-300" />
                <h3 className="text-lg font-semibold text-white">Question Types</h3>
              </div>
              <div className="space-y-3">
                {[
                  { value: 'multiple_choice', label: 'Multiple Choice' },
                  { value: 'character_matching', label: 'Character Matching' },
                  { value: 'true_false', label: 'True/False' }
                ]?.map(type => {
                  const isSelected = questionTypes?.includes(type?.value);
                  return (
                    <button
                      key={type?.value}
                      onClick={() => handleQuestionTypeToggle(type?.value)}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                        isSelected
                          ? 'bg-purple-500 text-white' :'bg-white/5 text-purple-100 hover:bg-white/10'
                      }`}
                    >
                      {type?.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Generate Button */}
        <div className="mt-8 flex justify-center">
          <button
            onClick={handleGenerateQuiz}
            disabled={generating || !selectedPlay || selectedScenes?.length === 0}
            className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold text-lg hover:from-purple-700 hover:to-pink-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3 shadow-xl"
          >
            {generating ? (
              <>
                <Loader2 className="w-6 h-6 animate-spin" />
                Generating Quiz with AI...
              </>
            ) : (
              <>
                <Sparkles className="w-6 h-6" />
                Generate Quiz
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIQuizGenerator;